// DocumentsPage — AI 학습자료 등록. 문서 업로드·중복차단·확정(RAG/LLM 학습자료 수집 1단계).
// frontend/src/pages/DocumentsPage.jsx
import { useState, useEffect, useRef } from 'react';
import { documentAPI } from '../api/client';
import AppShell from '../components/AppShell';
import Announcements from '../components/Announcements';
import Pager from '../components/Pager';
import { IconAlertTriangle, IconBook2, IconBooks, IconChevronDown, IconChevronUp, IconFile, IconFileImport, IconInfoCircle, IconLoader2 } from '@tabler/icons-react';
import { Page, PageHeader, Button, Pill, Tag, DataTable, EmptyState, LoadingState, ErrorState } from '../components/ui';
import { card as cardBase, input as inputBase, RADIUS } from '../utils/uiStyles';
import { alertDialog, confirmDialog } from '../components/AppDialog';

// 백엔드는 UTC(시간대 표기 없음)로 저장 → UTC로 간주해 KST 날짜(YYYY-MM-DD)로 표시
const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d)) return '';
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
};

const STATUS_LABEL = {
  pending:  { text: '승인 대기', tone: 'yellow' },
  active:   { text: '활성',     tone: 'green' },
  archived: { text: '보관됨',   tone: 'gray' },
  rejected: { text: '반려',     tone: 'red' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.pending;
  return <Pill tone={s.tone}>{s.text}</Pill>;
}

export default function DocumentsPage() {
  const fileRef  = useRef(null);
  const decideRef = useRef(null);   // 유사문서 결정 대기용 resolver
  const [dragOver, setDragOver] = useState(false);   // 드래그 앤 드롭 업로드 시각 피드백

  // ── 내 문서 목록
  const [myDocs, setMyDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [groupDocs, setGroupDocs] = useState([]);
  const [groupDocsLoading, setGroupDocsLoading] = useState(true);
  const [docPage, setDocPage] = useState(1);
  const DOCS_PER = 10;

  // ── 업로드(대기열) 상태
  const [busy,       setBusy]       = useState(false);   // 대기열 처리 중
  const [progress,   setProgress]   = useState(null);    // { idx, total, name }
  const [reviewItem, setReviewItem] = useState(null);    // 유사문서 결정 대기 중인 업로드 응답
  const [resolution, setResolution] = useState('');
  const [supersedeId,setSupersedeId]= useState(null);
  const [summary,    setSummary]    = useState(null);    // 처리 결과 요약
  const [error,      setError]      = useState('');

  const [showGuide,  setShowGuide]  = useState(true);

  useEffect(() => { loadDocuments(); }, []);
  useEffect(() => { setDocPage(1); }, [myDocs.length]);   // 목록 변경 시 1페이지로
  const totalDocPages = Math.ceil(myDocs.length / DOCS_PER);
  const pagedDocs = myDocs.slice((docPage - 1) * DOCS_PER, docPage * DOCS_PER);
  const docStats = myDocs.reduce((acc, doc) => {
    acc.total += 1;
    acc[doc.status] = (acc[doc.status] || 0) + 1;
    return acc;
  }, { total: 0, pending: 0, active: 0, rejected: 0, archived: 0 });

  const loadMyDocs = async () => {
    setDocsLoading(true);
    try {
      const r = await documentAPI.myDocuments();
      setMyDocs(r.data);
    } catch { /* ignore */ } finally {
      setDocsLoading(false);
    }
  };

  const loadGroupDocs = async () => {
    setGroupDocsLoading(true);
    try {
      const r = await documentAPI.groupDocuments();
      setGroupDocs(r.data || []);
    } catch {
      setGroupDocs([]);
    } finally {
      setGroupDocsLoading(false);
    }
  };

  const loadDocuments = async () => {
    await Promise.all([loadMyDocs(), loadGroupDocs()]);
  };

  // ── 파일 선택 → 대기열 실행
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    runQueue(files);
  };

  // 드래그 앤 드롭 업로드 — 허용 확장자만 걸러 동일 대기열(runQueue)로
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const ALLOWED = ['.pdf', '.docx', '.xlsx', '.txt'];
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (!dropped.length) return;
    const ok = dropped.filter((f) => ALLOWED.some((ext) => f.name.toLowerCase().endsWith(ext)));
    if (ok.length < dropped.length) alertDialog(`${dropped.length - ok.length}개 파일은 형식이 맞지 않아 제외됩니다. (PDF · DOCX · XLSX · TXT만 가능)`);
    if (ok.length) runQueue(ok);
  };

  const cancelTemp = async (tempId) => {
    try { await documentAPI.confirmDocument({ temp_id: tempId, title: '', resolution: 'cancel' }); }
    catch { /* ignore */ }
  };

  const autoConfirm = async (data, resol, supId) => {
    await documentAPI.confirmDocument({
      temp_id:          data.temp_id,
      title:            data.suggested_title || data.filename || '문서',
      resolution:       resol,
      supersede_doc_id: supId || null,
      category:         data.classification?.category || '',
      doc_type:         data.classification?.doc_type || '',
      tags:             data.classification?.tags || [],
    });
  };

  // 파일 1건 처리 → 결과 문자열 반환
  const processOne = async (file) => {
    const fd = new FormData();
    fd.append('file', file);

    let data;
    try {
      data = (await documentAPI.uploadDocument(fd)).data;
    } catch (e) {
      return { type: 'failed', name: file.name, detail: e.response?.data?.detail };
    }

    if (data.duplicate) return { type: 'dupe', name: file.name };

    if (data.similar_docs && data.similar_docs.length > 0) {
      // 유사 문서가 있으면 사용자 결정 대기
      setResolution('');
      setSupersedeId(null);
      setReviewItem(data);
      const decision = await new Promise((resolve) => { decideRef.current = resolve; });
      decideRef.current = null;
      setReviewItem(null);

      if (decision.action === 'cancel') {
        await cancelTemp(data.temp_id);
        return { type: 'canceled', name: file.name };
      }
      try {
        await autoConfirm(data, decision.action, decision.supersedeId);
        return { type: decision.action === 'supersede' ? 'superseded' : 'registered', name: file.name };
      } catch (e) {
        return { type: 'failed', name: file.name, detail: e.response?.data?.detail };
      }
    }

    // 유사 문서 없음 → 즉시 등록
    try {
      await autoConfirm(data, 'new', null);
      return { type: 'registered', name: file.name };
    } catch (e) {
      return { type: 'failed', name: file.name, detail: e.response?.data?.detail };
    }
  };

  const runQueue = async (files) => {
    setError('');
    setSummary(null);
    setBusy(true);
    const acc = { registered: 0, superseded: 0, dupe: 0, canceled: 0, failed: 0, dupeNames: [], failedNames: [] };
    for (let i = 0; i < files.length; i++) {
      setProgress({ idx: i + 1, total: files.length, name: files[i].name });
      const r = await processOne(files[i]);
      if (r.type === 'registered') acc.registered++;
      else if (r.type === 'superseded') acc.superseded++;
      else if (r.type === 'dupe') { acc.dupe++; acc.dupeNames.push(r.name); }
      else if (r.type === 'canceled') acc.canceled++;
      else { acc.failed++; acc.failedNames.push(r.name + (r.detail ? ` (${r.detail})` : '')); }
    }
    setProgress(null);
    setBusy(false);
    setSummary(acc);
    await loadDocuments();
  };

  // 유사문서 결정 적용
  const applyDecision = () => {
    if (!resolution) return;
    if (resolution === 'supersede' && !supersedeId) { setError('대체할 문서를 선택해 주십시오.'); return; }
    setError('');
    if (decideRef.current) decideRef.current({ action: resolution, supersedeId });
  };

  const handleDelete = async (docId) => {
    if (!(await confirmDialog('삭제하겠습니까?', { danger: true }))) return;
    try {
      await documentAPI.deleteDocument(docId);
      await loadMyDocs();
    } catch (err) {
      alertDialog(err.response?.data?.detail || '삭제 중 오류가 발생했습니다.');
    }
  };

  // ── 스타일 상수(카드 = 공용 card 한 벌 · 제목 h2 = SectionTitle 과 같은 값)
  const Card = { ...cardBase, marginBottom: '16px' };
  const H2   = { fontSize: '14px', fontWeight: 620, color: 'var(--gw-text, #32312e)' };
  const Sel  = { ...inputBase, cursor: 'pointer', background: 'var(--gw-surface, #fff)' };
  // 유사문서 결정 — 고르지 않았거나 대체할 문서를 안 골랐으면 «적용하고 다음» 을 끈다
  const off  = !resolution || (resolution === 'supersede' && !supersedeId);

  return (
    <AppShell app="documents" style={{ fontFamily: 'var(--gw-font)', background: 'var(--gw-bg, #fff)' }}>
      <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
      <Announcements scope="documents" />

      <Page>
        <PageHeader icon={IconBooks} tone="yellow" title="AI 학습자료 등록" />

        {error && (
          <div style={{ marginBottom: '16px' }}><ErrorState message={error} /></div>
        )}

        {/* ── 등록 안내 ── */}
        <div style={Card}>
          <div aria-label={showGuide ? '학습자료 등록 안내 접기' : '학습자료 등록 안내 펼치기'} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', cursor: 'pointer' }} onClick={() => setShowGuide(v => !v)}>
            <h2 style={{ ...H2, margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}><IconBook2 size={18} stroke={1.8} />어떤 자료를 등록하면 좋나요?</h2>
            <span style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>{showGuide ? <>접기 <IconChevronUp size={14} stroke={1.8} /></> : <>펼치기 <IconChevronDown size={14} stroke={1.8} /></>}</span>
          </div>
          {showGuide && (
            <div style={{ marginTop: '14px' }}>
              <p style={{ fontSize: '13.5px', color: 'var(--gw-text, #32312e)', lineHeight: 1.7, margin: 0 }}>
                여기 올린 자료는 사내 AI가 업무 질문에 답할 때 <strong>근거로 삼고</strong>(RAG), 우리 회사 업무에 맞게 <strong>AI를 학습시키는 데</strong>(LLM 학습) 함께 쓰입니다.
                잘 정리된 자료 한 건이 여러 사람이 반복해서 묻던 질문을 대신 답해 줍니다.
              </p>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', margin: '12px 0 0', fontSize: '12.5px', color: 'var(--gw-secondary, #64635f)' }}>
                {['① 파일 등록(승인 대기)', '② 관리자 확인·승인', '③ AI가 내용을 읽어 정리', '④ 학습자료로 적합하면 AI 답변에 활용'].map((s, i, a) => (
                  <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ whiteSpace: 'nowrap' }}><Tag tone="blue">{s}</Tag></span>
                    {i < a.length - 1 && <span style={{ color: 'var(--gw-muted, #92918d)' }}>›</span>}
                  </span>
                ))}
              </div>

              <p style={{ fontSize: '13px', fontWeight: 620, color: 'var(--gw-blue, #2383e2)', margin: '18px 0 4px' }}>이런 자료가 좋아요</p>
              <p style={{ fontSize: '12.5px', color: 'var(--gw-muted, #92918d)', margin: '0 0 8px' }}>공통점은 <strong>누가 물어볼 만한 답이 글로 적혀 있는 문서</strong>라는 점입니다.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['업무 매뉴얼·절차서', '풀필먼트 입출고 절차, WMS·OMS 사용법, 정산 처리 가이드'],
                  ['규정·정책', '사내 규정, 근태·휴가 정책, 보안 지침'],
                  ['FAQ·응대 자료', '자주 묻는 질문과 답, CS 응대 스크립트, 화주 문의 대응 사례'],
                  ['양식·템플릿', '보고서·계약서 양식 (작성 예시가 함께 있으면 더 좋습니다)'],
                  ['업무 지식·용어', '화주별 특이사항, 사내 용어집, 센터·시스템 구성 설명'],
                  ['잘 작성된 예시', '모범 이메일·보고서, 좋은 질문-답변 쌍 (AI가 말투와 형식을 배웁니다)'],
                ].map(([k, ex]) => (
                  <div key={k} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '13px', flexWrap: 'wrap' }}>
                    <span style={{ flexShrink: 0, fontWeight: '600', color: 'var(--gw-text, #32312e)', minWidth: '170px' }}>{k}</span>
                    <span style={{ color: 'var(--gw-secondary, #64635f)' }}>예: {ex}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: '13px', fontWeight: 620, color: 'var(--gw-secondary, #64635f)', margin: '18px 0 8px' }}>이런 자료는 도움이 덜 돼요</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['숫자·표만 있는 원본 데이터', '무엇을 뜻하는지 설명이 없으면 AI가 읽어내지 못합니다. 요약·해설이 붙은 문서로 올려 주세요.'],
                  ['녹취를 그대로 옮긴 글', '결론이 정리된 회의록이 훨씬 낫습니다. 녹음 파일은 AI 노트 메뉴를 이용해 주세요.'],
                  ['화면 캡처만 모아 둔 문서', '글자가 거의 없으면 답변 근거로 쓰기 어렵습니다.'],
                  ['개인 메모·업무와 무관한 자료', '검색 결과만 흐려집니다.'],
                ].map(([k, why]) => (
                  <div key={k} style={{ display: 'flex', gap: '8px', alignItems: 'baseline', fontSize: '13px', flexWrap: 'wrap' }}>
                    <span style={{ flexShrink: 0, fontWeight: '600', color: 'var(--gw-text, #32312e)', minWidth: '170px' }}>{k}</span>
                    <span style={{ color: 'var(--gw-secondary, #64635f)' }}>{why}</span>
                  </div>
                ))}
              </div>

              <p style={{ fontSize: '13px', fontWeight: 620, color: 'var(--gw-blue, #2383e2)', margin: '18px 0 8px' }}>파일 준비 팁</p>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--gw-secondary, #64635f)', lineHeight: 1.8 }}>
                <li><strong>스캔한 PDF도 괜찮습니다.</strong> AI가 이미지 속 글자를 읽어냅니다. 다만 흐리거나 기울어진 스캔은 정확도가 떨어지니 가급적 선명한 파일로 올려 주세요. 쪽수가 아주 많은 스캔본은 <strong>앞쪽 120쪽까지만</strong> 읽히니 나눠 올려 주세요.</li>
                <li><strong>암호가 걸린 PDF는 읽지 못합니다.</strong> 암호를 푼 뒤 올려 주세요.</li>
                <li>사내 <strong>DRM으로 잠긴 파일</strong>도 등록되지 않습니다. 보안 해제 후 올려 주세요.</li>
                <li>한 파일에 여러 주제가 섞여 있으면, <strong>주제별로 나눠</strong> 올리는 편이 답변 정확도에 좋습니다.</li>
              </ul>

              <p style={{ fontSize: '13px', fontWeight: 620, color: 'var(--gw-yellow-ink, #8d6d28)', margin: '18px 0 8px' }}>주의해 주세요</p>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px', color: 'var(--gw-secondary, #64635f)', lineHeight: 1.8 }}>
                <li><strong>개인정보·기밀은 가린 뒤 올려 주세요.</strong> 주민번호·계좌·연락처·비밀번호 등이 담겨 있으면, 나중에 AI 답변에 그대로 인용될 수 있습니다.</li>
                <li><strong>최신본만 등록해 주세요.</strong> 옛 버전이 함께 남아 있으면 AI가 지난 내용을 근거로 답할 수 있습니다. 같은 문서의 새 버전이라면, <strong>내가 전에 올린 파일과 이름이 비슷할 때</strong> 업로드 중에 <strong>대체</strong> 선택창이 뜹니다. 다른 사람이 올린 자료는 직접 대체할 수 없으니 관리자에게 알려 주세요.</li>
                <li><strong>똑같은 파일</strong>을 다시 올리면 자동으로 걸러집니다(내용이 같아도 다시 저장한 파일은 다른 파일로 봅니다).</li>
                <li>형식은 <strong>PDF · DOCX · XLSX · TXT</strong>, 파일당 <strong>최대 50MB</strong>입니다. (한글 HWP·구형 DOC는 아직 지원하지 않습니다 — PDF로 저장해 올려 주세요.)</li>
                <li>제목은 <strong>파일명으로 자동 지정</strong>됩니다. 파일명을 알아보기 쉽게 지어 두면 나중에 찾기 편합니다.</li>
                <li>등록 직후에는 <strong>승인 대기</strong>로 표시되고, 관리자가 승인해 <strong>활성</strong>이 되면 그때부터 AI가 참고합니다. 학습자료로 맞지 않으면 <strong>반려</strong>될 수 있고, 목록에 사유가 함께 표시됩니다.</li>
              </ul>
            </div>
          )}
        </div>

        {/* ── 업로드 영역 ── */}
        {!busy && !reviewItem && (
          <div style={Card}>
            <h2 style={{ ...H2, marginBottom: '6px' }}>파일 등록</h2>
            <p style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '16px' }}>여러 파일을 한 번에 선택할 수 있습니다. 그룹원이 이미 올린 자료인지 먼저 확인해 주세요.</p>
            <div
              aria-label="학습자료 끌어다 놓기 영역"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); if (e.dataTransfer?.types?.includes('Files') && !dragOver) setDragOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); if (e.currentTarget === e.target) setDragOver(false); }}
              onDrop={handleDrop}
              style={{ border: `2px dashed ${dragOver ? 'var(--gw-blue-chip, #d3e7f9)' : 'var(--gw-border, #e5e5e2)'}`, borderRadius: RADIUS.lg, padding: 'clamp(24px, 6vw, 40px)', textAlign: 'center', cursor: 'pointer', background: dragOver ? 'var(--gw-blue-soft, #eaf3fc)' : 'var(--gw-subtle, #fbfbfa)', transition: 'border-color .15s, background .15s' }}
              onMouseEnter={e => { if (!dragOver) e.currentTarget.style.borderColor = 'var(--gw-blue-chip, #d3e7f9)'; }}
              onMouseLeave={e => { if (!dragOver) e.currentTarget.style.borderColor = 'var(--gw-border, #e5e5e2)'; }}
            >
              <div style={{ color: 'var(--gw-secondary, #64635f)', marginBottom: '12px', display: 'flex', justifyContent: 'center' }}>{dragOver ? <IconFileImport size={36} stroke={1.7} /> : <IconFile size={36} stroke={1.7} />}</div>
              <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--gw-text, #32312e)', marginBottom: '6px' }}>{dragOver ? '여기에 놓으면 업로드됩니다' : '클릭하거나 파일을 끌어다 놓으세요 (여러 개 가능)'}</p>
              <p style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)' }}>PDF · DOCX · XLSX · TXT · 파일당 최대 50MB</p>
            </div>
            <input ref={fileRef} aria-label="학습자료 파일 선택" type="file" multiple accept=".pdf,.docx,.xlsx,.txt" onChange={handleFileSelect} style={{ display: 'none' }} />
          </div>
        )}

        {/* ── 진행 표시 ── */}
        {busy && !reviewItem && (
          <div style={{ ...Card, textAlign: 'center', padding: '40px' }}>
            <div style={{ color: 'var(--gw-secondary, #64635f)', marginBottom: '14px', display: 'flex', justifyContent: 'center' }}><IconLoader2 size={30} stroke={1.8} /></div>
            <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--gw-text, #32312e)' }}>
              등록 중입니다... {progress ? `(${progress.idx}/${progress.total})` : ''}
            </p>
            {progress && <p style={{ fontSize: '13px', color: 'var(--gw-muted, #92918d)', marginTop: '6px' }}>{progress.name}</p>}
          </div>
        )}

        {/* ── 유사 문서 처리(검토는 이 단계만) ── */}
        {reviewItem && (
          <div style={Card}>
            <h2 style={{ ...H2, marginBottom: '4px' }}>유사 문서가 있습니다</h2>
            <p style={{ fontSize: '13px', color: 'var(--gw-secondary, #64635f)', marginBottom: '8px' }}>
              {progress ? `(${progress.idx}/${progress.total}) ` : ''}<b style={{ color: 'var(--gw-text, #32312e)' }}>{reviewItem.filename}</b> 와 유사한 기존 문서가 있습니다. 처리 방법을 선택해 주십시오.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
              {reviewItem.similar_docs.map((doc) => (
                <div key={doc.id} style={{ border: '1px solid var(--gw-line, #eeeeec)', borderRadius: RADIUS.lg, padding: '12px 16px', background: 'var(--gw-subtle, #fbfbfa)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--gw-text, #32312e)', margin: '0 0 4px', wordBreak: 'break-word' }}>{doc.title}</p>
                    <p style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', margin: 0 }}>
                      {doc.category} · {doc.doc_type} · {fmtDate(doc.created_at)} · 유사도 {Math.round(doc.similarity * 100)}%
                    </p>
                  </div>
                  <StatusBadge status={doc.status} />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                ['new',       '다른 문서입니다',            '기존 문서와 별개로 새로 등록합니다.'],
                ['supersede', '내 문서가 최신입니다',        '선택한 기존 문서를 보관 처리하고 내 문서로 대체합니다.'],
                ['cancel',    '기존 문서가 최신입니다 (건너뛰기)', '이 파일은 등록하지 않고 넘어갑니다.'],
              ].map(([val, label, desc]) => (
                <label key={val} style={{ display: 'flex', gap: '10px', padding: '10px 14px', borderRadius: RADIUS.lg, border: `1px solid ${resolution === val ? 'var(--gw-blue-chip, #d3e7f9)' : 'var(--gw-border, #e5e5e2)'}`, cursor: 'pointer', background: resolution === val ? 'var(--gw-blue-soft, #eaf3fc)' : 'var(--gw-surface, #fff)', alignItems: 'flex-start' }}>
                  <input type="radio" name="resolution" value={val} checked={resolution === val}
                    onChange={() => { setResolution(val); if (val !== 'supersede') setSupersedeId(null); }}
                    style={{ marginTop: '2px', accentColor: 'var(--gw-blue, #2383e2)' }} />
                  <div>
                    <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 2px' }}>{label}</p>
                    <p style={{ fontSize: '12px', color: 'var(--gw-secondary, #64635f)', margin: 0 }}>{desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {resolution === 'supersede' && (
              <div style={{ marginTop: '14px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--gw-text, #32312e)', display: 'block', marginBottom: '6px' }}>대체할 문서 선택</label>
                <select aria-label="대체할 문서 선택" value={supersedeId || ''} onChange={e => setSupersedeId(Number(e.target.value) || null)} style={Sel}>
                  <option value="">선택...</option>
                  {reviewItem.similar_docs.map(d => (
                    <option key={d.id} value={d.id}>{d.title} ({fmtDate(d.created_at)})</option>
                  ))}
                </select>
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <Button variant="primary" onClick={applyDecision} disabled={off}>적용하고 다음</Button>
            </div>
          </div>
        )}

        {/* ── 처리 결과 요약 ── */}
        {summary && !busy && !reviewItem && (
          <div style={{ ...Card, background: 'var(--gw-green-soft, #dcebe2)', border: '1px solid var(--gw-green-soft, #dcebe2)' }}>
            <p style={{ fontSize: '14px', fontWeight: '700', color: 'var(--gw-green-ink, #426b55)', margin: '0 0 6px' }}>처리 완료</p>
            <p style={{ fontSize: '13px', color: 'var(--gw-text, #32312e)', margin: 0 }}>
              등록 {summary.registered}건 · 대체 {summary.superseded}건 · 중복 {summary.dupe}건 · 건너뜀 {summary.canceled}건{summary.failed ? ` · 실패 ${summary.failed}건` : ''}
            </p>
            {summary.dupeNames.length > 0 && (
              <p style={{ fontSize: '12px', color: 'var(--gw-yellow-ink, #8d6d28)', margin: '6px 0 0', wordBreak: 'break-word' }}>중복(미등록): {summary.dupeNames.join(', ')}</p>
            )}
            {summary.failedNames.length > 0 && (
              <p style={{ fontSize: '12px', color: 'var(--gw-red-ink, #a4564d)', margin: '4px 0 0', wordBreak: 'break-word' }}>실패: {summary.failedNames.join(', ')}</p>
            )}
          </div>
        )}

        {/* ── 내 문서 목록 ── */}
        <div style={Card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <h2 style={{ ...H2, margin: 0 }}>내가 올린 문서</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
              <Pill tone="gray" dot={false}>전체 {docStats.total}</Pill>
              <Pill tone="yellow" dot={false}>대기 {docStats.pending}</Pill>
              <Pill tone="green" dot={false}>활성 {docStats.active}</Pill>
            </div>
          </div>
          {docsLoading ? (
            <LoadingState>불러오는 중...</LoadingState>
          ) : myDocs.length === 0 ? (
            <EmptyState description="등록한 문서가 없습니다." />
          ) : (
            <>
            <DataTable rowKey="id" minWidth="760px" stackOnMobile rows={pagedDocs} columns={[
              { key: 'title', label: '제목', render: (doc) => (
                // 표 칸은 nowrap 이라 반려 사유가 잘리지 않게 이 칸만 줄바꿈을 연다
                <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <div style={{ fontWeight: '600', color: 'var(--gw-text, #32312e)' }}>{doc.title}</div>
                  <div style={{ fontSize: '12px', color: 'var(--gw-muted, #92918d)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.original_name}</div>
                  {doc.reject_reason && (
                    <div style={{ fontSize: '12px', color: 'var(--gw-red-ink, #a4564d)', marginTop: '2px' }}>반려 사유: {doc.reject_reason}</div>
                  )}
                  {doc.classify_status === 'processing' && (
                    <div style={{ fontSize: '12px', color: 'var(--gw-yellow-ink, #8d6d28)', marginTop: '2px' }}>AI 분류 중…</div>
                  )}
                  {doc.classify_status === 'failed' && (
                    <div style={{ fontSize: '12px', color: 'var(--gw-red-ink, #a4564d)', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}><IconAlertTriangle size={14} stroke={1.8} />분류 실패{doc.classify_reason ? `: ${doc.classify_reason}` : ''}</div>
                  )}
                  {doc.classify_status !== 'processing' && doc.classify_status !== 'failed' && (doc.rag_suitable != null || doc.finetune_suitable != null) && (
                    <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                      {doc.rag_suitable != null && <Tag tone={doc.rag_suitable ? 'green' : 'gray'}>RAG {doc.rag_suitable ? '적합' : '부적합'}</Tag>}
                      {doc.finetune_suitable != null && <Tag tone={doc.finetune_suitable ? 'blue' : 'gray'}>LLM {doc.finetune_suitable ? '적합' : '부적합'}</Tag>}
                      {doc.classify_reason && <span title={doc.classify_reason} style={{ color: 'var(--gw-muted, #92918d)', cursor: 'help', display: 'inline-flex', alignItems: 'center' }}><IconInfoCircle size={13} stroke={1.8} /></span>}
                    </div>
                  )}
                </div>
              ) },
              { key: 'category', label: '분류', width: '120px', render: (doc) => doc.category || '-' },
              { key: 'doc_type', label: '유형', width: '120px', render: (doc) => doc.doc_type || '-' },
              { key: 'status', label: '상태', width: '104px', render: (doc) => <StatusBadge status={doc.status} /> },
              { key: 'created_at', label: '등록일', width: '104px', render: (doc) => fmtDate(doc.created_at) },
              { key: 'actions', label: '', width: '72px', render: (doc) => (doc.status === 'pending' ? (
                <Button variant="danger" size="sm" aria-label={`학습자료 삭제 문서 ${doc.id}: ${doc.title || doc.original_name || '제목 없음'}`} onClick={() => handleDelete(doc.id)}>삭제</Button>
              ) : null) },
            ]} />
            <Pager page={docPage} totalPages={totalDocPages} onPage={setDocPage} />
            </>
          )}
        </div>

        {(groupDocsLoading || groupDocs.length > 0) && (
          <div style={Card} data-testid="group-documents-section">
            <h2 style={{ ...H2, margin: '0 0 14px' }}>우리 그룹 등록 자료</h2>
            {groupDocsLoading ? (
              <LoadingState>불러오는 중...</LoadingState>
            ) : (
              <DataTable rowKey="id" minWidth="640px" stackOnMobile rows={groupDocs} columns={[
                { key: 'title', label: '제목', render: (doc) => (
                  <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', fontWeight: '600', color: 'var(--gw-text, #32312e)' }}>{doc.title}</div>
                ) },
                { key: 'uploader', label: '업로더', width: '140px', render: (doc) => doc.uploader || '-' },
                { key: 'status', label: '상태', width: '104px', render: (doc) => <StatusBadge status={doc.status} /> },
                { key: 'created_at', label: '등록일', width: '104px', render: (doc) => fmtDate(doc.created_at) },
              ]} />
            )}
          </div>
        )}

      </Page>
      </div>
    </AppShell>
  );
}
